import React from 'react'

import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import Divider from '@material-ui/core/Divider'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Grid from '@material-ui/core/Grid'
import IconButton from '@material-ui/core/IconButton'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import withStyles from '@material-ui/core/styles/withStyles'
import Delete from '@material-ui/icons/Delete'
import { Field } from 'react-final-form'

import TextField from '../../ui/Fields/TextField'
import TextFieldWithMask from '../../ui/Fields/TextFieldWithMask'
import Select from '../../ui/Fields/Select'
import DatePicker from '../../ui/Fields/DatePicker'
import styles from '@theme/styles/forms'
import SearchSelect from '../../ui/Fields/SearchSelect'
import PropTypes from 'prop-types'

const AddressFields = ({ classes, prefix, base }) => {
  const nameFor = (field) => base ? `${base}.${prefix}_${field}` : `${prefix}_${field}`

  return (
    <Grid container spacing={16}>
      <Grid item xs={12}>
        <Field
          component={TextField}
          className={classes.form__fieldWithError}
          margin='normal'
          fullWidth
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          label={I18n.t(`activerecord.attributes.v4/organization.${prefix}_street`)}
          name={nameFor('street')}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <Field
          component={TextField}
          className={classes.form__fieldWithError}
          margin='normal'
          fullWidth
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          label={I18n.t(`activerecord.attributes.v4/organization.${prefix}_city`)}
          name={nameFor('city')}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <Field
          component={TextField}
          className={classes.form__fieldWithError}
          margin='normal'
          fullWidth
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          label={I18n.t(`activerecord.attributes.v4/organization.${prefix}_state`)}
          name={nameFor('state')}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Field
          component={TextFieldWithMask}
          mask='99999-999'
          className={classes.form__fieldWithError}
          margin='normal'
          fullWidth
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          label={I18n.t(`activerecord.attributes.v4/organization.${prefix}_zip_code`)}
          name={nameFor('zip_code')}
        />
      </Grid>
    </Grid>
  )
}

const ContractFields = ({ classes, index, contract, contractTypes, onRemove, removable }) => {
  const prefix = `contracts[${index}]`

  return (
    <Paper elevation={0} className={classes.form__contractBlock} data-testid={`contract-block-${index}`}>
      <div className={classes.form__contractBlock__header}>
        <Typography component='h4' variant='subtitle1'>
          {I18n.t('v4/organization.form.contracts.title', { number: index + 1 })}
        </Typography>
        <IconButton
          onClick={onRemove}
          disabled={!removable}
          aria-label={I18n.t('v4/organization.form.contracts.remove')}
          data-testid={`remove-contract-${index}`}
        >
          <Delete />
        </IconButton>
      </div>

      <div className={classes.form__field__wrapper}>
        <Field
          component={TextField}
          className={classes.form__fieldWithError}
          margin='normal'
          fullWidth
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          label={I18n.t('activerecord.attributes.v4/organization.legal_name')}
          name={`${prefix}.legal_name`}
        />
      </div>
      <div className={classes.form__field__wrapper}>
        <Field
          component={TextFieldWithMask}
          mask='99.999.999/9999-99'
          className={classes.form__fieldWithError}
          margin='normal'
          fullWidth
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          label={I18n.t('activerecord.attributes.v4/organization.tax_id')}
          name={`${prefix}.tax_id`}
        />
      </div>
      <div className={classes.form__field__wrapper}>
        <Field
          component={TextField}
          className={classes.form__fieldWithError}
          margin='normal'
          fullWidth
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          label={I18n.t('activerecord.attributes.v4/organization.contract_code')}
          name={`${prefix}.contract_code`}
        />
      </div>

      <Grid container spacing={16}>
        <Grid item xs={12} md={4}>
          <Field
            component={Select}
            className={classes.form__fieldWithError}
            InputLabelProps={{ shrink: true }}
            label={I18n.t('activerecord.attributes.v4/organization.contract_type')}
            name={`${prefix}.contract_type`}
            margin='normal'
            fields={{ id: 'id', name: 'name' }}
            data={contractTypes}
            fullWidth
            variant="outlined"
          >
          </Field>
        </Grid>
        <Grid item xs={12} md={4}>
          <Field
            component={TextField}
            className={classes.form__fieldWithError}
            margin='normal'
            fullWidth
            type='number'
            InputLabelProps={{ shrink: true }}
            InputProps={{ inputProps: { min: "1" } }}
            variant="outlined"
            label={I18n.t('activerecord.attributes.v4/organization.contract_period_months')}
            name={`${prefix}.contract_period_months`}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Field
            component={DatePicker}
            className={classes.form__fieldWithError}
            margin='normal'
            fullWidth
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            label={I18n.t('activerecord.attributes.v4/organization.renewal_date')}
            name={`${prefix}.renewal_date`}
          />
        </Grid>
      </Grid>

      <Typography component='h5' variant='subtitle2' gutterBottom>
        {I18n.t('v4/organization.form.sections.shipping_address')}
      </Typography>
      <AddressFields classes={classes} prefix='shipping' base={prefix} />

      <Field name={`${prefix}.billing_same_as_shipping`} type='checkbox'>
        {({ input }) => (
          <FormControlLabel
            control={
              <Checkbox
                checked={!!input.checked}
                onChange={input.onChange}
                color='primary'
                inputProps={{ 'data-testid': `billing-same-as-shipping-${index}` }}
              />
            }
            label={I18n.t('activerecord.attributes.v4/organization.billing_same_as_shipping')}
          />
        )}
      </Field>

      {
        !contract.billing_same_as_shipping &&
        <React.Fragment>
          <Typography component='h5' variant='subtitle2' gutterBottom>
            {I18n.t('v4/organization.form.sections.billing_address')}
          </Typography>
          <AddressFields classes={classes} prefix='billing' base={prefix} />
        </React.Fragment>
      }
    </Paper>
  )
}

const Form = ({
  handleSubmit,
  classes,
  title,
  organizationKinds,
  predominantCultures,
  customerSuccessManagers,
  timezoneList,
  tenants,
  hasTenant,
  statusOptions,
  contractTypes,
  globalAdmin,
  values,
  form,
  o
}) => (
  <form className={classes.form} onSubmit={handleSubmit} data-testid="organization-form">
    <Paper className={classes.form__wrapper} elevation={0}>
      <header className={classes.form__header}>
        <Typography component='h2' variant='h5'>
          {title}
        </Typography>
      </header>
      <main className={classes.form__fields}>
        <Typography component='h3' variant='h6' gutterBottom>
          {I18n.t('v4/organization.form.sections.organization')}
        </Typography>
        <div className={classes.form__field__wrapper}>
          <Field
            component={TextField}
            className={classes.form__fieldWithError}
            margin='normal'
            fullWidth
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            label={I18n.t('activerecord.attributes.v4/organization.name')}
            name='name'
            required
          />
        </div>
        <div className={classes.form__field__wrapper}>
          <Field
            component={Select}
            className={classes.form__fieldWithError}
            InputLabelProps={{ shrink: true, required: true }}
            label={I18n.t('activerecord.attributes.v4/organization.kind_text')}
            name='kind'
            margin='normal'
            fields={{ id: 'id', name: 'name' }}
            data={organizationKinds}
            fullWidth
            variant="outlined"
            required
          >
          </Field>
        </div>
        <div className={classes.form__field__wrapper}>
          <Field
            component={Select}
            className={classes.form__fieldWithError}
            InputLabelProps={{ shrink: true, required: true }}
            label={I18n.t('activerecord.attributes.v4/organization.predominant_culture')}
            name='predominant_culture'
            margin='normal'
            fields={{ id: 'id', name: 'name' }}
            data={predominantCultures}
            fullWidth
            variant="outlined"
            required
          >
          </Field>
        </div>
        {
          timezoneList &&
          <div className={classes.form__field__wrapper}>
            <Field
              component={SearchSelect}
              fullWidth
              dataTestid='timezone'
              margin='normal'
              data={timezoneList}
              className={classes.form__fieldWithError}
              fields={{ label: 'name', value: 'name' }}
              variant='outlined'
              name='timezone'
              isClearable
              placeholder={I18n.t('actions.search')}
              autocomplete={true}
              textFieldProps={{
                label: I18n.t('activerecord.attributes.v4/organization.timezone'),
                InputLabelProps: {
                  shrink: true,
                }
              }}
            >
            </Field>
          </div>
        }
        {
          !hasTenant && <div className={classes.form__field__wrapper}>
            <Field
              component={Select}
              className={classes.form__fieldWithError}
              InputLabelProps={{ shrink: true, required: true }}
              label={I18n.t('activerecord.attributes.v4/organization.tenant_id')}
              name='tenant_id'
              margin='normal'
              fields={{ id: 'id', name: 'name' }}
              data={tenants}
              fullWidth
              variant="outlined"
              required
            >
            </Field>
          </div>
        }
        {
          globalAdmin &&
          <Grid container spacing={16}>
            <Grid item xs={12} md={6}>
              <Field
                component={TextField}
                className={classes.form__fieldWithError}
                margin='normal'
                fullWidth
                type='number'
                InputLabelProps={{ shrink: true }}
                InputProps={{ inputProps: { min: "1" } }}
                variant="outlined"
                label={I18n.t('activerecord.attributes.v4/organization.max_area_size')}
                name='max_area_size'
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field
                component={TextField}
                className={classes.form__fieldWithError}
                margin='normal'
                fullWidth
                type='number'
                InputLabelProps={{ shrink: true }}
                InputProps={{ inputProps: { min: "1" } }}
                variant="outlined"
                label={I18n.t('activerecord.attributes.v4/organization.max_total_area_size')}
                name='max_total_area_size'
              />
            </Grid>
          </Grid>
        }
        {
          customerSuccessManagers &&
          <div className={classes.form__field__wrapper}>
            <Field
              component={SearchSelect}
              fullWidth
              dataTestid='customer_success_manager'
              margin='normal'
              data={customerSuccessManagers}
              className={classes.form__fieldWithError}
              fields={{ label: 'name', value: 'id' }}
              variant='outlined'
              name='customer_success_manager'
              isClearable
              placeholder={I18n.t('actions.search')}
              autocomplete={true}
              textFieldProps={{
                label: I18n.t('activerecord.attributes.v4/organization.customer_success_manager'),
                InputLabelProps: {
                  shrink: true,
                }
              }}
            >
            </Field>
          </div>
        }

        <Divider className={classes.form__divider} />

        <Typography component='h3' variant='h6' gutterBottom>
          {I18n.t('v4/organization.form.sections.registration')}
        </Typography>
        <Grid container spacing={16}>
          <Grid item xs={12} md={statusOptions ? 6 : 12}>
            <Field
              component={TextField}
              className={classes.form__fieldWithError}
              margin='normal'
              fullWidth
              type='number'
              InputLabelProps={{ shrink: true }}
              InputProps={{ inputProps: { min: "0" } }}
              variant="outlined"
              label={I18n.t('activerecord.attributes.v4/organization.customer_code')}
              name='customer_code'
              required
            />
          </Grid>
          {
            statusOptions &&
            <Grid item xs={12} md={6}>
              <Field
                component={Select}
                className={classes.form__fieldWithError}
                InputLabelProps={{ shrink: true, required: true }}
                label={I18n.t('activerecord.attributes.v4/organization.status')}
                name='status'
                margin='normal'
                fields={{ id: 'id', name: 'name' }}
                data={statusOptions}
                fullWidth
                variant="outlined"
                required
              >
              </Field>
            </Grid>
          }
        </Grid>
        {
          globalAdmin &&
          <React.Fragment>
            {
              (values.contracts || []).map((contract, index) => (
                <ContractFields
                  key={contract.id || `new-${index}`}
                  classes={classes}
                  index={index}
                  contract={contract}
                  contractTypes={contractTypes}
                  removable={(values.contracts || []).length > 1}
                  onRemove={() =>
                    form.change('contracts', values.contracts.filter((_, i) => i !== index))
                  }
                />
              ))
            }
            <Button
              color='primary'
              variant='outlined'
              data-testid='add-contract'
              onClick={() =>
                form.change('contracts', [...(values.contracts || []), { billing_same_as_shipping: false }])
              }
            >
              + {I18n.t('v4/organization.form.contracts.add')}
            </Button>
          </React.Fragment>
        }
      </main>
      <footer className={classes.form__actions}>
        <Button
          className={classes.form__action}
          color='primary'
          type='submit'
          variant='contained'
        >
          {title}
        </Button>
        {
          !hasTenant &&
          <Button
            className={classes.form__action}
            color='secondary'
            href={`/v4/o/${o}/admin/organizations`}
          >
            {I18n.t('actions.back')}
          </Button>
        }
      </footer>
    </Paper>
  </form>
)

const Styles = theme => ({
  ...styles(theme),
  form__divider: {
    margin: `${theme.spacing.unit * 3}px 0`
  },
  form__contractBlock: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing.unit * 2,
    margin: `${theme.spacing.unit * 2}px 0`
  },
  form__contractBlock__header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between'
  }
})

export default withStyles(Styles)(Form)

Form.propTypes = {
  handleSubmit: PropTypes.func,
  classes: PropTypes.object,
  title: PropTypes.string,
  organizationKinds: PropTypes.array,
  predominantCultures: PropTypes.array,
  customerSuccessManagers: PropTypes.array,
  timezoneList: PropTypes.array,
  tenants: PropTypes.array,
  hasTenant: PropTypes.bool,
  statusOptions: PropTypes.array,
  contractTypes: PropTypes.array,
  globalAdmin: PropTypes.bool,
  values: PropTypes.object,
  form: PropTypes.object,
  o: PropTypes.string
}

Form.defaultProps = {
  contractTypes: [],
  values: {}
}
